import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';
import { SortDirection } from '@/types';
import { formatDate } from '@/utils';

import { CloseIcon, PlusIcon } from '@/assets/icons';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { DEVICE_TYPE_OPTIONS } from '@/features/ipad-configs/constants';
import { useDevicesSearching } from '@/features/ipad-configs/hooks';
import { IIpadConfigsSearchingPayload } from '@/features/ipad-configs/interfaces';
import { getDeviceTypeLabel, getDeviceTypeVariant } from '@/features/ipad-configs/utils';
import { DeviceType } from '@/features/ipad-configs/enums';

const IpadConfigList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);
  const [deviceTypes, setDeviceTypes] = useState<string[]>(
    searchParams.get('types')?.split(',').filter(Boolean) || [],
  );
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || 'lastModified');
  const [isDescending, setIsDescending] = useState<boolean>(searchParams.get('desc') !== 'false');
  const previousFilterKeyRef = useRef<string | null>(null);

  // Handle column sorting
  const handleSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setIsDescending(true);
    } else {
      setSortBy(columnKey);
      setIsDescending(direction === 'desc');
    }
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // Throttle filters and sorting
  const throttledDeviceTypes = useThrottle(deviceTypes, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IIpadConfigsSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    const deviceTypesCount = Object.keys(DeviceType).length / 2; // Divide by 2 because of how enums are compiled in TS

    if (
      throttledDeviceTypes &&
      throttledDeviceTypes.length > 0 &&
      throttledDeviceTypes.length < deviceTypesCount
    ) {
      payload.deviceType = throttledDeviceTypes.map((type) => Number(type));
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledDeviceTypes,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching, isError } = useDevicesSearching(apiPayload);

  const devices = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata.totalPages || 0, [data?.metadata.totalPages]);
  const totalItems = useMemo(() => data?.metadata.totalItems || 0, [data?.metadata.totalItems]);

  const isFiltered = useMemo(() => deviceTypes.length > 0, [deviceTypes]);

  const isEmpty = useMemo(() => devices.length === 0, [devices]);

  const onClearFilters = () => {
    setSearchQuery('');
    setDeviceTypes([]);
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (deviceTypes.length) params.set('types', deviceTypes.join(','));
    if (sortBy !== 'lastModified') params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    setSearchParams(params, { replace: true });
  }, [searchQuery, deviceTypes, sortBy, isDescending, setSearchParams, currentPage]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledDeviceTypes,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, throttledDeviceTypes, throttledSortBy, throttledIsDescending]);

  if (isError) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
  }

  return (
    <div className="w-full flex flex-col items-start justify-start">
      <h1 className="font-bold text-[32px] text-neutral-n10">iPad Configurations</h1>
      <div className="flex flex-row flex-wrap gap-2 items-start justify-between w-full mt-8 mb-5">
        <div className="flex flex-row flex-wrap items-start justify-start gap-2">
          <Search
            placeholder="Search by Serial Number or Device Name"
            className="gap-2 !h-11 !w-[330px] sm:!w-[360px]"
            style={{ height: '44px', width: '360px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={DEVICE_TYPE_OPTIONS}
            defaultValue={deviceTypes}
            placeholder="Device Type"
            onValueChange={(values) => {
              setDeviceTypes(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[300px]"
          />
          {isFiltered && (
            <Button
              variant="link"
              prefixIcon={<CloseIcon className="w-5 h-5" />}
              onClick={onClearFilters}
              className="!pl-1 py-3"
            >
              Clear
            </Button>
          )}
        </div>
        <Button
          variant="primary"
          prefixIcon={<PlusIcon />}
          className="font-semibold text-sm px-4 rounded-lg whitespace-nowrap text-ellipsis !h-11 w-[235px]"
          onClick={() => navigate(AppRoutes.IPAD_CONFIGURATION_CREATION)}
        >
          Create New Configuration
        </Button>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult />
        ) : (
          <>
            <Table className="mt-4" size="lg">
              <TableHeader>
                <TableRow>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'serialNumber' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('serialNumber', direction)}
                  >
                    iPad Serial Number
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'deviceName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('deviceName', direction)}
                    className="w-[50%]"
                  >
                    Device Name
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'deviceType' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('deviceType', direction)}
                  >
                    Device Type
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModified' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModified', direction)}
                  >
                    Last Modified
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModifiedBy' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModifiedBy', direction)}
                  >
                    Last Modified By
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.serialNumber} rowId={device.serialNumber}>
                    <TableCell className="font-medium underline text-brand-primary-base">
                      <Link
                        to={generatePath(AppRoutes.IPAD_CONFIGURATION_DETAILS, {
                          id: device.serialNumber,
                        })}
                      >
                        {device.serialNumber}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium w-[50%] max-w-[984px]">
                      <span className="block truncate" title={device.deviceName}>
                        {device.deviceName ? device.deviceName : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell cellType="tag" tagVariant={getDeviceTypeVariant(device.deviceType)}>
                      {getDeviceTypeLabel(device.deviceType)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatDate({
                        date: new Date(device.lastModified),
                        hasTime: true,
                      })}
                    </TableCell>
                    <TableCell className="font-medium max-w-[185px]">
                      <span className="block truncate" title={device.lastModifiedBy}>
                        {device.lastModifiedBy}
                      </span>
                    </TableCell>
                    <TableCell align="right" cellType="custom" className="!p-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="w-[63px] aspect-square"
                          asChild
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          side="left"
                          alignOffset={26}
                          sideOffset={-16}
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.IPAD_CONFIGURATION_DETAILS, {
                                  id: device.serialNumber,
                                }),
                              )
                            }
                          >
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.IPAD_CONFIGURATION_UPDATE, {
                                  id: device.serialNumber,
                                }),
                              )
                            }
                          >
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
export default IpadConfigList;
